import {
  SING_MAX_MIDI,
  SING_MIN_MIDI,
  SING_PITCH_BY_ID,
  SING_PITCHES,
  type SingPitchId,
} from './clingCantaConfig';

export const SING_MIN_HZ = 150;
export const SING_MAX_HZ = 680;
/** Margen Hz para plegar octavas al rango jugable La3–Do5. */
export const SING_DETECT_MIN_HZ = 200;
export const SING_DETECT_MAX_HZ = 550;

/**
 * Si YIN da un Hz dentro del rango vocal del juego, confiar en él.
 * Si no, plegar octavas arriba/abajo (como resolveOctave del metalófono).
 */
export function resolveSingOctave(rawFreq: number): number {
  if (rawFreq <= 0) return rawFreq;
  if (rawFreq >= SING_DETECT_MIN_HZ && rawFreq <= SING_DETECT_MAX_HZ) {
    return rawFreq;
  }
  let f = rawFreq;
  for (let i = 0; i < 5; i++) {
    if (f >= SING_DETECT_MIN_HZ && f <= SING_DETECT_MAX_HZ) return f;
    if (f < SING_DETECT_MIN_HZ) f *= 2;
    else f /= 2;
  }
  return Math.min(SING_DETECT_MAX_HZ, Math.max(SING_DETECT_MIN_HZ, f));
}

/** Coloca el MIDI en La3–Do5 eligiendo la octava más coherente. */
export function foldMidiToSingRange(midi: number, hint?: number | null): number {
  const candidates: number[] = [];
  for (let k = -3; k <= 3; k++) {
    const m = midi + k * 12;
    if (m >= SING_MIN_MIDI && m <= SING_MAX_MIDI) candidates.push(m);
  }
  if (!candidates.length) {
    return Math.min(SING_MAX_MIDI, Math.max(SING_MIN_MIDI, midi));
  }
  if (hint != null && Number.isFinite(hint)) {
    return candidates.reduce((best, m) =>
      Math.abs(m - hint) < Math.abs(best - hint) ? m : best
    );
  }
  return candidates.reduce((best, m) =>
    Math.abs(m - midi) < Math.abs(best - midi) ? m : best
  );
}
export const SING_VOLUME_THRESHOLD = 0.006;
export const SING_MATCH_CENTS = 120;
export const SING_MATCH_CENTS_STRICT = 95;

export function freqToMidi(freq: number): number {
  return 69 + 12 * Math.log2(freq / 440);
}

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function clampMidi(midi: number): number {
  return Math.min(SING_MAX_MIDI, Math.max(SING_MIN_MIDI, midi));
}

export function nearestSingPitch(midi: number, allowed: SingPitchId[]): SingPitchId | null {
  if (!allowed.length) return null;
  let best: SingPitchId | null = null;
  let bestDiff = Infinity;
  for (const id of allowed) {
    const def = SING_PITCH_BY_ID[id];
    const diff = Math.abs(def.midi - midi);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = id;
    }
  }
  if (best == null) return null;
  const cents = Math.abs(SING_PITCH_BY_ID[best].midi - midi) * 100;
  return cents <= SING_MATCH_CENTS_STRICT ? best : null;
}

/** Siempre devuelve el carril más cercano (para UI y guía). */
export function nearestSingPitchLoose(midi: number, allowed: SingPitchId[]): SingPitchId | null {
  if (!allowed.length) return null;
  let best: SingPitchId | null = null;
  let bestDiff = Infinity;
  for (const id of allowed) {
    const def = SING_PITCH_BY_ID[id];
    const diff = Math.abs(def.midi - midi);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = id;
    }
  }
  return best;
}


/** Altura fija por carril en el piano roll (px). */
export const CLING_LANE_HEIGHT = 30;
export const CLING_ROLL_PADDING = 6;
export const CLING_NOTE_HEIGHT = 22;

export function clingRollContentHeight(laneCount: number): number {
  return CLING_ROLL_PADDING * 2 + laneCount * CLING_LANE_HEIGHT;
}

export function pitchLaneTop(pitchId: SingPitchId, lanes: SingPitchId[]): number {
  const idx = lanes.indexOf(pitchId);
  if (idx < 0) return CLING_ROLL_PADDING;
  return CLING_ROLL_PADDING + idx * CLING_LANE_HEIGHT;
}

export function pitchLaneCenterY(pitchId: SingPitchId, lanes: SingPitchId[]): number {
  return pitchLaneTop(pitchId, lanes) + CLING_LANE_HEIGHT / 2;
}

export function midiToLaneY(midi: number, lanes: SingPitchId[]): number {
  const clamped = clampMidi(midi);
  const minMidi = SING_PITCH_BY_ID[lanes[lanes.length - 1]!]?.midi ?? SING_MIN_MIDI;
  const maxMidi = SING_PITCH_BY_ID[lanes[0]!]?.midi ?? SING_MAX_MIDI;
  const t = (clamped - minMidi) / Math.max(1, maxMidi - minMidi);
  const contentH = clingRollContentHeight(lanes.length);
  const inner = contentH - CLING_ROLL_PADDING * 2;
  return CLING_ROLL_PADDING + (1 - t) * inner;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function allSingPitchIds(): SingPitchId[] {
  return SING_PITCHES.map((p) => p.id);
}
